import React, { useState } from 'react';
import {
  Row,
  Col,
  Table,
  Form,
  Button,
  Pagination,
  Container,
} from 'react-bootstrap';
import { useGetSuppliesQuery } from '../../slices/suppliesApiSlice';
import { useGetExpensesQuery } from '../../slices/expensesApiSlice';
import Loader from '../../components/Loader';
import Message from '../../components/Message';
import * as XLSX from 'xlsx';

const SupplierBalanceScreen = () => {
  const [filter, setFilter] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [expandedSupplier, setExpandedSupplier] = useState(null);

  const { data, isLoading, error, refetch } = useGetSuppliesQuery(pageNumber);
  const today = new Date().toISOString().split('T')[0];

  const { data: expensesData, isLoading: loadingExpenses } = useGetExpensesQuery({
    startDate: '1900-01-01',
    endDate: today,
  });

  const supplierEntries = (data?.supplies || [])
    .map(s => s.supplierDetails)
    .filter(details => details?.name);

  const supplierMap = {};
  supplierEntries.forEach(details => {
    if (details.name && !supplierMap[details.name]) {
      supplierMap[details.name] = details;
    }
  });

  const filteredSuppliers = Object.values(supplierMap).filter(s =>
    s.name.toLowerCase().includes(filter.toLowerCase())
  );

  const getTotalPaid = (supplierName) =>
    expensesData?.expenses
      ?.filter(
        e =>
          e.accountName === supplierName &&
          e.category?.toLowerCase() === 'supplypayment'
      )
      .reduce((sum, e) => sum + Number(e.amount || 0), 0) || 0;

  const getSupplierSummary = (supplierName) => {
    const supplies = data?.supplies?.filter(s => s.supplierDetails?.name === supplierName) || [];
    let totalCost = 0, cash = 0, credit = 0, donation = 0;

    supplies.forEach(s => {
      const cost = Number(s.cardCost || 0);
      totalCost += cost;
      if (s.status === 'Cash') cash += cost;
      else if (s.status === 'Credit') credit += cost;
      else if (s.status === 'Donation') donation += cost;
    });

    return { totalCost, cash, credit, donation };
  };

  const exportToExcel = () => {
    const rows = filteredSuppliers.map(({ name }) => {
      const { totalCost, cash, credit, donation } = getSupplierSummary(name);
      const paid = getTotalPaid(name);
      const balance = credit + paid;

      return {
        Supplier: name,
        'Total Supply Cost': totalCost.toFixed(2),
        'Cash Supply': cash.toFixed(2),
        'Credit Supply': credit.toFixed(2),
        'Donation Supply': donation.toFixed(2),
        'Total Paid': paid.toFixed(2),
        'Balance Due': balance.toFixed(2),
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'SupplierBalances');
    XLSX.writeFile(workbook, 'SupplierBalances.xlsx');
  };

  const paginationHandler = (page) => {
    setPageNumber(page);
  };

  const summaryTotals = filteredSuppliers.reduce(
    (acc, supplier) => {
      const summary = getSupplierSummary(supplier.name);
      const paid = getTotalPaid(supplier.name);
      const balance = summary.credit - paid;
      acc.totalCost += summary.totalCost;
      acc.cash += summary.cash;
      acc.credit += summary.credit;
      acc.donation += summary.donation;
      acc.paid += paid;
      acc.balance += balance;
      return acc;
    },
    { totalCost: 0, cash: 0, credit: 0, donation: 0, paid: 0, balance: 0 }
  );

  return (
    <>
      <Row className="mb-4">
        <Col><h1>Supplier Balances</h1></Col>
      </Row>

      {/* Filters */}
      <Row className="mb-3">
        <Col md={3}>
          <Form.Control
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            placeholder="Start Date"
          />
        </Col>
        <Col md={3}>
          <Form.Control
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            placeholder="End Date"
          />
        </Col>
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Filter by supplier name"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Col>
        <Col md={2} className="text-end">
          <Button onClick={() => refetch()}>Reset</Button>
        </Col>
      </Row>

      {/* Sticky Summary */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          background: '#fff',
          borderBottom: '1px solid #dee2e6',
          padding: '10px 0',
          marginBottom: '15px'
        }}
      >
        <Container fluid>
          <Row className="text-center fw-bold">
            <Col>Total Supply Cost: ${summaryTotals.totalCost.toFixed(2)}</Col>
            <Col>Cash Supply: ${summaryTotals.cash.toFixed(2)}</Col>
            <Col>Credit Supply: ${summaryTotals.credit.toFixed(2)}</Col>
            <Col>Donation Supply: ${summaryTotals.donation.toFixed(2)}</Col>
            <Col>Total Paid: ${summaryTotals.paid.toFixed(2)}</Col>
            <Col>Balance Due: ${summaryTotals.balance.toFixed(2)}</Col>
          </Row>
        </Container>
      </div>

      {/* Data Table */}
      {isLoading || loadingExpenses ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">
          {error?.data?.message || 'Error loading data'}
        </Message>
      ) : filteredSuppliers.length === 0 ? (
        <Message variant="info">No supplier balances to display.</Message>
      ) : (
        <>
          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th>Supplier Name</th>
                <th>Total Supply Cost</th>
                <th>Cash Supply</th>
                <th>Credit Supply</th>
                <th>Donation Supply</th>
                <th>Total Paid</th>
                <th>Balance Due</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSuppliers.map(({ name }) => {
                const { totalCost, cash, credit, donation } = getSupplierSummary(name);
                const paid = getTotalPaid(name);
                const balance = credit + paid;

                return (
                  <React.Fragment key={name}>
                    <tr>
                      <td>{name}</td>
                      <td>{totalCost.toFixed(2)}</td>
                      <td>{cash.toFixed(2)}</td>
                      <td>{credit.toFixed(2)}</td>
                      <td>{donation.toFixed(2)}</td>
                      <td>{paid.toFixed(2)}</td>
                      <td style={{ color: balance > 0 ? 'red' : 'green' }}>
                        {balance.toFixed(2)}
                      </td>
                      <td>
                        <Button
                          variant="info"
                          size="sm"
                          onClick={() =>
                            setExpandedSupplier(expandedSupplier === name ? null : name)
                          }
                        >
                          {expandedSupplier === name ? 'Hide History' : 'Show History'}
                        </Button>
                      </td>
                    </tr>

                    {expandedSupplier === name && (
                      <tr>
                        <td colSpan="8">
                          <Table size="sm" bordered responsive>
                            <thead>
                              <tr>
                                <th>Date</th>
                                <th>Note</th>
                                <th>Cost</th>
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(data?.supplies || [])
                                .filter(s => s.supplierDetails?.name === name)
                                .map(s => (
                                  <tr key={s._id}>
                                    <td>{new Date(s.date).toLocaleDateString()}</td>
                                    <td>{s.note || '—'}</td>
                                    <td>${Number(s.cardCost || 0).toFixed(2)}</td>
                                    <td>{s.status}</td>
                                  </tr>
                                ))}
                            </tbody>
                          </Table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </Table>

          {/* Pagination */}
          <Pagination>
            {[...Array(data?.pages || 1).keys()].map(x => (
              <Pagination.Item
                key={x + 1}
                active={x + 1 === pageNumber}
                onClick={() => paginationHandler(x + 1)}
              >
                {x + 1}
              </Pagination.Item>
            ))}
          </Pagination>

          {/* Export Button */}
          <Row className="mt-3">
            <Col className="text-end">
              <Button variant="success" onClick={exportToExcel}>
                Export to Excel
              </Button>
            </Col>
          </Row>
        </>
      )}
    </>
  );
};

export default SupplierBalanceScreen;